import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Languages, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { nombreEspecialidad, useEspecialidades } from '@/features/preguntas/queries';
import { fechaHora } from '@/lib/formato';
import { cn } from '@/lib/utils';

import {
  ESTADOS_CASO,
  PROVEEDORES_IA,
  useCasoIa,
  useCasosIa,
  useEstadisticasIa,
  type CasoIa,
  type ContenidoCaso,
  type EstadoCaso,
} from './queries';

/** Editor del contenido del caso: texto, preguntas, opciones (marca la correcta) y justificaciones. */
function EditorContenido({
  valor,
  onCambio,
  solo,
}: {
  valor: ContenidoCaso;
  onCambio: (c: ContenidoCaso) => void;
  solo: boolean;
}) {
  const pregunta = (i: number, c: Partial<ContenidoCaso['questions'][number]>) =>
    onCambio({
      ...valor,
      questions: valor.questions.map((q, j) => (j === i ? { ...q, ...c } : q)),
    });
  return (
    <div className="space-y-4">
      <Campo etiqueta="Título">
        <input
          className="input-vidrio"
          disabled={solo}
          value={valor.title}
          onChange={(e) => onCambio({ ...valor, title: e.target.value })}
        />
      </Campo>
      <Campo etiqueta="Caso clínico">
        <textarea
          className="input-vidrio min-h-36"
          disabled={solo}
          value={valor.clinicalCase}
          onChange={(e) => onCambio({ ...valor, clinicalCase: e.target.value })}
        />
      </Campo>
      {valor.questions.map((q, i) => (
        <fieldset key={i} className="space-y-3 rounded-2xl border border-[var(--separador)] p-4">
          <legend className="px-1 text-sm font-extrabold">Pregunta {i + 1}</legend>
          <textarea
            className="input-vidrio min-h-16"
            disabled={solo}
            value={q.prompt}
            aria-label="Enunciado"
            onChange={(e) => pregunta(i, { prompt: e.target.value })}
          />
          {q.options.map((o, k) => (
            <div
              key={k}
              className="grid gap-1 sm:grid-cols-[auto_1fr_1fr] sm:items-center sm:gap-2"
            >
              <label className="flex items-center gap-1 text-xs font-bold">
                <input
                  type="radio"
                  name={`correcta-${i}`}
                  className="h-4 w-4 accent-[var(--color-verde-500)]"
                  disabled={solo}
                  checked={o.isCorrect}
                  onChange={() =>
                    pregunta(i, {
                      options: q.options.map((x, m) => ({ ...x, isCorrect: m === k })),
                    })
                  }
                />
                Correcta
              </label>
              <input
                className="input-vidrio"
                disabled={solo}
                value={o.text}
                aria-label={`Opción ${k + 1}`}
                onChange={(e) =>
                  pregunta(i, {
                    options: q.options.map((x, m) =>
                      m === k ? { ...x, text: e.target.value } : x,
                    ),
                  })
                }
              />
              <input
                className="input-vidrio text-xs"
                disabled={solo}
                value={o.rationale}
                placeholder="Por qué"
                aria-label={`Razón de la opción ${k + 1}`}
                onChange={(e) =>
                  pregunta(i, {
                    options: q.options.map((x, m) =>
                      m === k ? { ...x, rationale: e.target.value } : x,
                    ),
                  })
                }
              />
            </div>
          ))}
          <textarea
            className="input-vidrio min-h-20 text-sm"
            disabled={solo}
            value={q.explanation}
            aria-label="Justificación"
            placeholder="Justificación"
            onChange={(e) => pregunta(i, { explanation: e.target.value })}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              className="input-vidrio text-sm"
              disabled={solo}
              value={q.pivot}
              placeholder="Pivote"
              aria-label="Pivote"
              onChange={(e) => pregunta(i, { pivot: e.target.value })}
            />
            <input
              className="input-vidrio text-sm"
              disabled={solo}
              value={q.bibliography}
              placeholder="Bibliografía"
              aria-label="Bibliografía"
              onChange={(e) => pregunta(i, { bibliography: e.target.value })}
            />
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function DetalleCaso({ caso, onCambio }: { caso: CasoIa; onCambio: (id: number) => void }) {
  const qc = useQueryClient();
  const editable =
    useCan('ia', 'editar') && (caso.status === 'review' || caso.status === 'rejected');
  const puedeActuar = useCan('ia', 'editar');
  const esp = useEspecialidades();
  const [contenido, setContenido] = useState(caso.content);
  const [especialidad, setEspecialidad] = useState(caso.specialtyId);
  const [aprobar, setAprobar] = useState(false);
  const [flags, setFlags] = useState({
    isOnline: true,
    isOnlineDemo: false,
    isQuiz: false,
    isQuizDemo: false,
  });
  const [rechazar, setRechazar] = useState(false);
  const [regenerar, setRegenerar] = useState(false);
  const [comentarios, setComentarios] = useState('');
  const ruta = { params: { path: { id: String(caso.id) } } };
  const refrescar = (c: CasoIa) => {
    qc.setQueryData(['ia', 'caso', c.id], c);
    void qc.invalidateQueries({ queryKey: ['ia', 'casos'] });
    void qc.invalidateQueries({ queryKey: ['ia', 'stats'] });
  };
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const cambiado =
    JSON.stringify(contenido) !== JSON.stringify(caso.content) || especialidad !== caso.specialtyId;

  const guardar = useMutation({
    mutationFn: () =>
      unwrap<CasoIa>(
        api.PATCH('/api/v1/admin/ai/cases/{id}', {
          ...ruta,
          body: { content: contenido, specialtyId: especialidad },
        }),
      ),
    onSuccess: (c) => {
      refrescar(c);
      toast.success('Cambios guardados.');
    },
    onError: err,
  });
  const aprobarM = useMutation({
    mutationFn: async () => {
      if (cambiado)
        await unwrap(
          api.PATCH('/api/v1/admin/ai/cases/{id}', {
            ...ruta,
            body: { content: contenido, specialtyId: especialidad },
          }),
        );
      return unwrap<CasoIa>(
        api.POST('/api/v1/admin/ai/cases/{id}/approve', { ...ruta, body: flags }),
      );
    },
    onSuccess: (c) => {
      refrescar(c);
      setAprobar(false);
      toast.success(
        `Publicado en el banco: ${c.publishedQuestionIds.map((i) => `#${i}`).join(', ')}`,
      );
    },
    onError: err,
  });
  const estado = (accion: 'reject' | 'restore', notes?: string) => {
    const body = notes ? { notes } : {};
    return unwrap<CasoIa>(
      accion === 'reject'
        ? api.POST('/api/v1/admin/ai/cases/{id}/reject', { ...ruta, body })
        : api.POST('/api/v1/admin/ai/cases/{id}/restore', { ...ruta, body }),
    );
  };
  const rechazarM = useMutation({
    mutationFn: (n: string) => estado('reject', n),
    onSuccess: (c) => {
      refrescar(c);
      setRechazar(false);
    },
    onError: err,
  });
  const restaurarM = useMutation({
    mutationFn: () => estado('restore'),
    onSuccess: refrescar,
    onError: err,
  });
  const regenerarM = useMutation({
    mutationFn: () =>
      unwrap<CasoIa>(
        api.POST('/api/v1/admin/ai/cases/{id}/regenerate', {
          ...ruta,
          body: { feedback: comentarios },
        }),
      ),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['ia'] });
      setRegenerar(false);
      toast.success('Nueva versión generada.');
      onCambio(c.id);
    },
    onError: err,
  });
  const traducirM = useMutation({
    mutationFn: () => unwrap<CasoIa>(api.POST('/api/v1/admin/ai/cases/{id}/translate', ruta)),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['ia'] });
      toast.success('Versión en inglés creada (en revisión).');
      onCambio(c.id);
    },
    onError: err,
  });

  return (
    <section className="panel-vidrio space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">
            Caso #{caso.id} · {ESTADOS_CASO[caso.status]}
          </h2>
          <p className="text-xs text-texto-3">
            {PROVEEDORES_IA[caso.provider ?? ''] ?? caso.provider} · {caso.model} ·{' '}
            {caso.language === 'en' ? 'inglés' : 'español'} · {fechaHora(caso.createdAt)}
            {caso.parentCaseId && (
              <>
                {' '}
                · versión de{' '}
                <button
                  type="button"
                  className="font-semibold"
                  onClick={() => onCambio(caso.parentCaseId!)}
                >
                  #{caso.parentCaseId}
                </button>
              </>
            )}
          </p>
          {caso.reviewNotes && (
            <p className="mt-1 text-sm text-texto-2">Notas: {caso.reviewNotes}</p>
          )}
          {caso.publishedQuestionIds.length > 0 && (
            <p className="mt-1 text-sm">
              En el banco:{' '}
              {caso.publishedQuestionIds.map((i) => (
                <Link key={i} to={`/preguntas/${i}`} className="mr-2 font-semibold">
                  #{i}
                </Link>
              ))}
            </p>
          )}
        </div>
        {puedeActuar && (
          <div className="flex flex-wrap gap-2">
            {caso.status === 'review' && (
              <>
                <button type="button" className="btn-cs btn-exito" onClick={() => setAprobar(true)}>
                  <Check size={16} aria-hidden /> Aprobar
                </button>
                <button
                  type="button"
                  className="btn-cs btn-contorno"
                  onClick={() => setRechazar(true)}
                >
                  <X size={16} aria-hidden /> Rechazar
                </button>
              </>
            )}
            {(caso.status === 'rejected' || caso.status === 'archived') && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => restaurarM.mutate()}
              >
                Regresar a revisión
              </button>
            )}
            {caso.status !== 'approved' && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => setRegenerar(true)}
              >
                <RefreshCw size={16} aria-hidden /> Regenerar
              </button>
            )}
            {caso.language === 'es' && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                disabled={traducirM.isPending}
                onClick={() => traducirM.mutate()}
              >
                <Languages size={16} aria-hidden />{' '}
                {traducirM.isPending ? 'Traduciendo…' : 'Traducir al inglés'}
              </button>
            )}
          </div>
        )}
      </header>
      {caso.imageUrl && (
        <img src={caso.imageUrl} alt="Imagen del caso" className="max-h-64 rounded-xl" />
      )}
      {caso.sourceExcerpt && (
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold">Material usado</summary>
          <p className="mt-2 whitespace-pre-wrap text-texto-2">{caso.sourceExcerpt}</p>
        </details>
      )}
      <Campo etiqueta="Especialidad (obligatoria para aprobar)">
        <select
          className="input-vidrio max-w-md"
          disabled={!editable}
          value={especialidad ?? ''}
          onChange={(e) => setEspecialidad(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Sin especialidad</option>
          {esp.data?.map((e) => (
            <option key={e.id} value={e.id}>
              {nombreEspecialidad(e)}
            </option>
          ))}
        </select>
      </Campo>
      <EditorContenido valor={contenido} onCambio={setContenido} solo={!editable} />
      {editable && cambiado && (
        <button
          type="button"
          className="btn-cs btn-primario"
          disabled={guardar.isPending}
          onClick={() => guardar.mutate()}
        >
          Guardar cambios
        </button>
      )}

      <Modal
        abierto={aprobar}
        onCerrar={() => setAprobar(false)}
        titulo="Aprobar y publicar"
        descripcion="Se crean las preguntas en el banco. La primera lleva el caso; las demás quedan como subsecuentes."
      >
        <div className="space-y-3">
          {(
            [
              ['isOnline', 'Online'],
              ['isOnlineDemo', 'Online demo'],
              ['isQuiz', 'Quiz'],
              ['isQuizDemo', 'Quiz demo'],
            ] as const
          ).map(([k, t]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-azul-600)]"
                checked={flags[k]}
                onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })}
              />{' '}
              {t}
            </label>
          ))}
          <p className="text-xs text-texto-3">El simulador y los exámenes usan todo el banco.</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-cs btn-contorno" onClick={() => setAprobar(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-cs btn-exito"
              disabled={aprobarM.isPending || !especialidad}
              onClick={() => aprobarM.mutate()}
            >
              Publicar
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmarAccion
        abierto={rechazar}
        onCerrar={() => setRechazar(false)}
        titulo="Rechazar caso"
        descripcion="Queda guardado como rechazado; puedes regresarlo a revisión o regenerarlo."
        textoBoton="Rechazar"
        peligro
        pedirMotivo="Motivo"
        ocupado={rechazarM.isPending}
        onConfirmar={(m) => rechazarM.mutate(m)}
      />
      <Modal
        abierto={regenerar}
        onCerrar={() => setRegenerar(false)}
        titulo="Regenerar con comentarios"
        descripcion="La versión actual se archiva y la nueva queda en revisión."
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            regenerarM.mutate();
          }}
        >
          <textarea
            className="input-vidrio min-h-28"
            required
            minLength={5}
            value={comentarios}
            placeholder="Qué hay que corregir…"
            onChange={(e) => setComentarios(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-cs btn-contorno"
              onClick={() => setRegenerar(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-cs btn-primario" disabled={regenerarM.isPending}>
              {regenerarM.isPending ? 'Regenerando…' : 'Regenerar'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

function Revision() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? 'review') as EstadoCaso;
  const page = Number(params.get('page') ?? '1') || 1;
  const seleccionado = params.get('caso') ? Number(params.get('caso')) : null;
  const lista = useCasosIa({ page, status });
  const caso = useCasoIa(seleccionado);
  const stats = useEstadisticasIa();
  const ir = (c: Record<string, string>) => setParams({ status, ...c }, { replace: true });
  return (
    <>
      <Encabezado titulo="Revisión de casos con IA" />
      <div className="mb-4 flex flex-wrap gap-2">
        {(Object.keys(ESTADOS_CASO) as EstadoCaso[]).map((s) => (
          <button
            key={s}
            type="button"
            className={cn('btn-cs', status === s ? 'btn-primario' : 'btn-vidrio')}
            onClick={() => setParams({ status: s }, { replace: true })}
          >
            {ESTADOS_CASO[s]}{' '}
            {stats.data && <span className="ml-1 opacity-75">({stats.data.byStatus[s]})</span>}
          </button>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_2fr]">
        <section className="space-y-2">
          {lista.isPending && <Cargando />}
          {lista.isError && (
            <ErrorCarga error={lista.error} onReintentar={() => void lista.refetch()} />
          )}
          {lista.data?.data.length === 0 && <Vacio titulo="No hay casos en este estado" />}
          {lista.data?.data.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => ir({ caso: String(c.id), page: String(page) })}
              className={cn(
                'card-vidrio block w-full p-3 text-left',
                seleccionado === c.id && 'ring-2 ring-cian-500',
              )}
            >
              <p className="font-bold">
                #{c.id} · {c.title ?? 'Sin título'}
              </p>
              <p className="text-xs text-texto-3">
                {c.specialtyName ?? 'Sin especialidad'} · {c.content.questions.length} pregunta(s) ·{' '}
                {c.language.toUpperCase()} · {fechaHora(c.createdAt)}
              </p>
            </button>
          ))}
          {lista.data && (
            <Paginador
              page={lista.data.page}
              pageSize={lista.data.pageSize}
              total={lista.data.total}
              onPage={(p) => ir({ page: String(p) })}
            />
          )}
        </section>
        <div>
          {seleccionado === null && <Vacio titulo="Elige un caso para revisarlo" />}
          {seleccionado !== null && caso.isPending && <Cargando />}
          {caso.isError && <ErrorCarga error={caso.error} />}
          {caso.data && (
            <DetalleCaso
              key={`${caso.data.id}-${caso.data.status}`}
              caso={caso.data}
              onCambio={(id) => ir({ caso: String(id), page: String(page) })}
            />
          )}
        </div>
      </div>
    </>
  );
}

export function RevisionPage() {
  return (
    <PermissionGate seccion="ia">
      <Revision />
    </PermissionGate>
  );
}
